import { useState, useRef, useCallback, useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { subscribe } from 'lib/pusher';
import { armJobCompletion } from 'lib/hooks/useJobCompletion';
import { useServerExport } from 'lib/hooks/useServerExport';
import { useLoadableAtomValue } from 'lib/hooks/useLoadableAtomValue';
import { sessionResourceAtom } from 'atoms/root';
import {
  calculateBaseAtom,
  selectedCalculationIdAtom,
  appliedConfigAtom,
  calculationResultReadyCalcIdAtom,
  currentConfigAtom,
  isDirtyAtom,
  fetchCalculationResultBaseAtom,
  mergeExecutiveSummaryAtom,
  evalExportPresetsLoadableAtom,
} from './atoms';
import type {
  AggregatedFreeCashFlowResultResponse,
  ExecutiveSummaryUpdatedEventPayload,
} from 'types/api-responses';
import {
  withCurrentViewPreset,
  type CalculateRequest,
  type CalculationConfig,
  type PusherNotifications,
  type EvalExportPresets,
  type ExportFormat,
  type ExportPreset,
} from './types';

// Delay before the recovery poll starts. Gives Pusher the first opportunity
// to deliver `calculation_result`; the poll only kicks in if the event was
// missed or delayed.
const STUCK_CHECK_DELAY_MS = 3 * 60 * 1000; // 3 minutes
// Interval between recovery polls once they start.
const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds
// Hard cap on total time spent waiting for a calculation. After this we surface a timeout
// error rather than polling indefinitely.
//
// It sits PAST the server's own `EvaluationCalculation::PROCESSING_TIMEOUT` (30 minutes), and
// the gap is the whole point (ONCALL-937). This page is the one that AUTHORS a verdict: on
// expiry it tells the user the run timed out, a sentence no server produced. Set equal to the
// server's timeout — as it was — a genuine ~30-minute run with a missed event is a coin flip
// between the client's fabricated message and the server's real one, decided by scheduler
// jitter. Five minutes of headroom means a reachable server always gets to speak first and the
// client's verdict is reached only when the server never answered at all. Mirrors the
// `RUN_RECOVERY_MAX_MS` reasoning on the new page, which lands on the same 35.
const MAX_POLL_DURATION_MS = 35 * 60 * 1000; // 35 minutes

// ---------------------------------------------------------------------------
// useCalculate — manages the full calculate lifecycle including Pusher.
// ---------------------------------------------------------------------------

/**
 * Hook that manages the full Eval 3.0 calculate lifecycle.
 * Handles two Pusher paths: new calculations (POST then subscribe)
 * and pending tile clicks (subscribe to existing channel).
 *
 * @param {number} packageId - The package ID.
 * @param {Function} onRefetchCalculations - Callback to refetch calculations
 *   for a specific workspace after a calculation completes. Receives the
 *   workspace ID that the calculation was submitted to.
 * @param {Function} [onWarning] - Optional callback invoked for each warning
 *   from the eval calculator (e.g. slots with missing basin/tier data).
 * @return {object} Calculation state and actions.
 */
export const useCalculate = (
  packageId: number,
  onRefetchCalculations: (wsId: number) => void,
  onWarning?: (message: string) => void
): {
  isCalculating: boolean;
  error: string | null;
  calculationWarnings: string[];
  triggerCalculation: (
    config: CalculateRequest,
    configOverride?: CalculationConfig
  ) => Promise<void>;
  resumePendingCalculation: (
    calcId: number,
    calculationId: string | null,
    notifications: PusherNotifications,
    createdAt: string,
    workspaceId: number,
    dispatched?: boolean
  ) => void;
  cancelCalculation: () => void;
} => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationWarnings, setCalculationWarnings] = useState<string[]>([]);

  const pendingCalcIdRef = useRef<string | null>(null);
  // The run's RECORD id, which — unlike the calculator uuid above — is known the moment the
  // POST returns. Since eval-start moved into a job (INS-12398) the uuid does not exist for
  // the first ~26s of a run, so "is a run pending?" can no longer be answered by the uuid:
  // it is null on a first run and the PREVIOUS run's on a re-run. This ref answers it.
  const pendingRecordIdRef = useRef<number | null>(null);
  // On a re-run, the uuid of the run being REPLACED. The 202 still carries it (its results
  // stay readable while the new run computes), and a late or retried webhook about it is
  // stamped with this same record — so the record-addressed branch below would otherwise
  // accept it and render the superseded run's numbers as this run's. The BE also refuses to
  // put such an event on the record channel; this is the client half of that guard, because a
  // correctness property this sharp should not rest on one side alone (INS-12398).
  const supersededCalcIdRef = useRef<string | null>(null);
  // Which SUBSCRIPTION is live. `calcId` cannot answer that: a re-run REUSES its record, so
  // every run of one evaluation shares a calcId and a record-id comparison passes for all of
  // them — letting an old run's in-flight poll act on the run that replaced it, which is the
  // exact interleave the identity guard exists to stop, one layer down. A token minted per
  // `subscribeToPusher` call is the only thing that separates them (ONCALL-937).
  const runTokenRef = useRef<symbol | null>(null);
  // `unbind` is REQUIRED here, matching `JobChannel`. Every channel this can hold has it
  // (pusher-js's `Channel`, the `fakeChannel` in `lib/pusher`, the test doubles), and making
  // it optional turned every unbind below into a silent no-op when it was absent — the exact
  // handler leak `boundHandlersRef` exists to prevent.
  const subscriptionRef = useRef<{
    unsubscribe: () => void;
    unbind: (event: string, handler: (data: never) => void) => void;
  } | null>(null);
  // pusher-js `Channel#unsubscribe()` only flips `subscribed` and sends the frame — it does
  // NOT unbind handlers, and `Pusher#subscribe` hands back the SAME Channel object for a name
  // already in the registry. That never bit while every run had its own
  // `eval_calculator_<uuid>` channel; the pre-dispatch channel is now `eval_calculator_<record
  // id>`, which is stable across re-runs of the same evaluation, so without unbinding, run N
  // leaves N-1 stale handlers bound for the life of the session (INS-12398).
  const boundHandlersRef = useRef<
    Array<{ event: string; handler: (data: never) => void }>
  >([]);
  const appliedConfigSnapshotRef = useRef<CalculationConfig | null>(null);
  const previousAppliedConfigRef = useRef<CalculationConfig | null>(null);
  // Disarms the shared reconcile triggers for the live run: the subscribe trigger and the
  // recovery poll (`lib/hooks/useJobCompletion`). One ref replaces the two timer handles this
  // hook used to keep, because the timers now live inside the shared arming call.
  const disarmRef = useRef<(() => void) | null>(null);

  const triggerCalculate = useSetAtom(calculateBaseAtom);
  const setSelectedCalcId = useSetAtom(selectedCalculationIdAtom(packageId));
  const setAppliedConfig = useSetAtom(appliedConfigAtom(packageId));
  const appliedConfig = useAtomValue(appliedConfigAtom(packageId));
  const setCalcResultReadyId = useSetAtom(
    calculationResultReadyCalcIdAtom(packageId)
  );
  const fetchCalculationResult = useSetAtom(fetchCalculationResultBaseAtom);
  const mergeExecutiveSummary = useSetAtom(
    mergeExecutiveSummaryAtom(packageId)
  );
  const currentConfig = useAtomValue(currentConfigAtom(packageId));
  const currentConfigRef = useRef(currentConfig);
  currentConfigRef.current = currentConfig;

  /**
   * Subscribes to a Pusher channel and handles calculation completion/error.
   * Also arms the shared recovery triggers (`lib/hooks/useJobCompletion`), which
   * re-read eval_result:
   *   - the instant the subscription is REGISTERED, which closes the window between
   *     the POST returning and the socket being ready — anything published in it is
   *     lost for good, because Pusher does not replay (ONCALL-937). This also re-fires
   *     after a reconnect, so a socket dropped mid-run heals the same way;
   *   - and, as a backstop for Pusher being unreachable entirely, STUCK_CHECK_DELAY_MS
   *     after the calculation was created and every POLL_INTERVAL_MS thereafter, until
   *     completion data appears, an error path fires, the caller cancels, or
   *     MAX_POLL_DURATION_MS elapses (timeout).
   *
   * @param {number} calcId - The numeric calculation ID.
   * @param {string} calculationId - The string calculation ID for stale event guard.
   * @param {PusherNotifications} notifications - Channel and event info.
   * @param {string} createdAt - ISO timestamp of when the calculation was created.
   * @param {number} workspaceId - Whose calculation list to refresh on completion.
   * @param {string} [supersededCalculationId] - On a re-run, the uuid being replaced.
   */
  // Unbind everything this hook bound on the previous subscription. See boundHandlersRef.
  const unbindAll = useCallback(() => {
    const channel = subscriptionRef.current;
    boundHandlersRef.current.forEach(({ event, handler }) => {
      channel?.unbind(event, handler);
    });
    boundHandlersRef.current = [];
  }, []);

  const bindTracked = useCallback(
    (
      channel: { bind: (event: string, handler: never) => void },
      event: string,
      handler: (data: never) => void
    ) => {
      channel.bind(event, handler as never);
      boundHandlersRef.current.push({ event, handler });
    },
    []
  );

  // Stops the shared triggers and forgets them. `armJobCompletion` unbinds only what it
  // itself bound, so this composes with `unbindAll` rather than replacing it.
  const disarmRecovery = useCallback(() => {
    disarmRef.current?.();
    disarmRef.current = null;
  }, []);

  // Cleanup subscription, the subscribe trigger and the recovery poll on unmount. Declared
  // here, below `unbindAll`, only so it can call it: `unsubscribe()` does NOT unbind (see
  // boundHandlersRef), so leaving without unbinding leaks this hook's handlers onto a channel
  // object pusher-js keeps in its registry and hands back by name.
  useEffect(() => {
    return () => {
      unbindAll();
      disarmRecovery();
      subscriptionRef.current?.unsubscribe();
      // Disarm the run. Stopping the triggers does not cancel a read already in flight, and
      // its continuation would otherwise still resolve — writing package-scoped atoms other
      // mounted consumers read, from a hook that no longer exists. Harmless while the poll
      // could not resolve without a uuid; not harmless now (ONCALL-937).
      pendingRecordIdRef.current = null;
      runTokenRef.current = null;
    };
  }, [unbindAll, disarmRecovery]);

  const subscribeToPusher = useCallback(
    (
      calcId: number,
      // Null before the run dispatches (INS-12398) — the uuid does not exist yet, so the
      // stale-event guard falls back to the record id until the start job announces it.
      calculationId: string | null,
      notifications: PusherNotifications,
      createdAt: string,
      // Whose calculation list to refresh once this run completes. A per-SUBSCRIPTION value,
      // like `runToken`, deliberately not a ref (ONCALL-937 review). It used to be
      // `requestedWorkspaceIdRef`, written only by `triggerCalculation` and read back through
      // a `!` — so every completion reached from `resumePendingCalculation` refreshed
      // workspace `null` and the nav tiles kept showing Processing until the user navigated
      // away. That path is the one this ticket made primary, so half the fix landed. The `!`
      // is what let it past typecheck; a required parameter cannot be forgotten SILENTLY —
      // tsc names every call site, which is how all 28 of them were found.
      workspaceId: number,
      supersededCalculationId?: string | null
    ) => {
      unbindAll();
      disarmRecovery();
      subscriptionRef.current?.unsubscribe();

      pendingCalcIdRef.current = calculationId || null;
      pendingRecordIdRef.current = calcId;
      supersededCalcIdRef.current = supersededCalculationId ?? null;
      // This subscription's identity. Everything closed over below compares against it, so a
      // callback from a PREVIOUS subscription — including a poll fetch already in flight —
      // can tell it is no longer the live run even when the record id matches.
      const runToken = Symbol('eval3-run');
      runTokenRef.current = runToken;
      const channel = subscribe(notifications.channel);
      subscriptionRef.current = channel;

      console.debug(
        '[Eval3 Pusher] subscribed to channel=%s, calcId=%d, calculationId=%s',
        notifications.channel,
        calcId,
        calculationId
      );

      // The BE's completion event name. It gates the two PAYLOAD-consuming handlers below and
      // nothing else: this used to `return` here, which also skipped the recovery triggers —
      // so a descriptor that arrived without an event name left the run with no completion
      // path AND no way back (ONCALL-937). The subscribe trigger and the poll depend on the
      // channel, not on this, so they are armed below the guard.
      const eventName = notifications.events[0]?.name;

      // `calculationIdFromRecord` is the recovery poll's seed of last resort (ONCALL-937).
      // Every other caller is a Pusher handler, which has already armed `pendingCalcIdRef`
      // from the event it is acting on. The poll has not: this run's uuid only ever reaches
      // the client over Pusher, so in the one case the poll exists for — a missed event — the
      // ref is still null and this bailed, leaving the spinner up on a run that had already
      // succeeded. The poll passes the id off the eval_result body it just read instead.
      //
      // That id is the RECORD's current `calculation_id`, which is this run's only once the
      // run has dispatched. Before that the record still addresses the run this one replaced
      // (the column is deliberately left in place so those results stay readable — INS-12398),
      // and stamping this run's results with it would put the superseded run's numbers on
      // screen as this one's — including on every cashflows-by-month read, which resolves by
      // that uuid. `ensure_calculation_id!` already 404s that window server-side; refusing it
      // here too keeps both halves of the guard, exactly as the Pusher handlers do.
      const resolveCompletion = (
        warnings: string[],
        calculationIdFromRecord?: string | null
      ) => {
        // Identity, not truthiness — and identity of the SUBSCRIPTION, not of the record. A
        // poll fetch already in flight when the user starts another run is not cancelled
        // (nothing aborts it), so its continuation runs against the NEXT run's refs; asking
        // only "is a run pending?" it would answer yes, resolve the new run with the old run's
        // numbers, and stop the new run's recovery timers — stranding it. The uuid-null bail
        // used to make that unreachable by accident, and the fallback below removes that
        // accident, so the guard has to be explicit.
        //
        // `calcId` is NOT sufficient here, and an earlier revision of this comment wrongly
        // said it was: a re-run reuses its record, so successive runs of one evaluation share
        // a calcId and a record-id comparison passes for both (ONCALL-937).
        if (runTokenRef.current !== runToken) return;
        const resolvedCalculationId =
          pendingCalcIdRef.current ?? calculationIdFromRecord ?? null;
        if (!resolvedCalculationId) return;
        if (resolvedCalculationId === supersededCalcIdRef.current) return;
        setSelectedCalcId(calcId);
        setCalcResultReadyId({
          id: calcId,
          calculationId: resolvedCalculationId,
        });
        if (warnings.length) {
          setCalculationWarnings(warnings);
          if (onWarning) {
            warnings.forEach((w) => onWarning(w));
          }
        } else {
          setCalculationWarnings([]);
        }
        onRefetchCalculations(workspaceId);
        setIsCalculating(false);
        setError(null);
        pendingCalcIdRef.current = null;
        pendingRecordIdRef.current = null;
        runTokenRef.current = null;
        // Deliberately no `unbindAll()`/`unsubscribe()` here, unlike `resolveError` and
        // `cancelCalculation`: `executive_summary_updated` is fired from
        // `Queued::TractsBlobJob` after the pipeline, so it legitimately lands after the
        // terminal event and is what `summaryHandler` is still bound for. Bounded, not
        // leaked — the next `subscribeToPusher` unbinds.
        disarmRecovery();
      };

      const resolveError = (message: string) => {
        // Same subscription-identity test as resolveCompletion, and for the same reason:
        // `capExceeded` is measured against THIS subscription's clock, so an old run's poll
        // resuming after a newer run subscribed would otherwise time out — and unsubscribe —
        // a run that is seconds old. As there, the record id cannot make that distinction
        // across re-runs of one evaluation; only the token can.
        if (runTokenRef.current !== runToken) return;
        setAppliedConfig(previousAppliedConfigRef.current);
        setError(message);
        setIsCalculating(false);
        pendingCalcIdRef.current = null;
        pendingRecordIdRef.current = null;
        runTokenRef.current = null;
        disarmRecovery();
        // Unbind BEFORE dropping the reference, or the next `unbindAll` finds null, silently
        // skips every unbind and empties its own bookkeeping — leaking this run's handlers for
        // the life of the session and stacking another set on every subsequent run.
        unbindAll();
        subscriptionRef.current?.unsubscribe();
        subscriptionRef.current = null;
      };

      const resultHandler = (data: {
        status: string;
        calculationId?: string;
        evaluationCalculationId?: number;
        errorMessage?: string;
        warnings?: string[];
      }) => {
        console.debug(
          '[Eval3 Pusher] received event=%s, data=%o, pendingCalcId=%s',
          eventName,
          data,
          pendingCalcIdRef.current
        );

        // Two ways an event can be addressed to this run (INS-12398). By calculator uuid,
        // as always — and, for anything published before this run had one, by record id on
        // the record's own channel. The start job's post-dispatch event is addressed that
        // second way AND carries the uuid it just assigned, which is what arms the first.
        // A run this one replaced. Never ours, however it is addressed.
        if (
          data.calculationId &&
          data.calculationId === supersededCalcIdRef.current
        ) {
          return;
        }

        // Only while we have no uuid. Once the job has told us this run's id, the uuid is
        // the sharper test, and accepting record-addressed events past that point would let
        // an event about a superseded run re-arm the ref with the wrong id.
        const addressedByRecord =
          pendingCalcIdRef.current === null &&
          typeof data.evaluationCalculationId === 'number' &&
          data.evaluationCalculationId === pendingRecordIdRef.current;
        if (
          !addressedByRecord &&
          data.calculationId !== pendingCalcIdRef.current
        ) {
          return;
        }
        if (addressedByRecord && data.calculationId) {
          pendingCalcIdRef.current = data.calculationId;
        }

        if (data.status === 'Completed') {
          resolveCompletion(data.warnings ?? []);
        } else if (data.status === 'Error') {
          resolveError(data.errorMessage ?? 'Calculation failed');
        }
      };
      if (eventName) {
        bindTracked(channel, eventName, resultHandler);
      }

      const summaryHandler = (payload: ExecutiveSummaryUpdatedEventPayload) => {
        // Same two addressing modes as the terminal handler above: by uuid once we know it,
        // by record id before that. The BE publishes these to both channels (INS-12398).
        const addressedByRecord =
          pendingCalcIdRef.current === null &&
          typeof payload.evaluationCalculationId === 'number' &&
          payload.evaluationCalculationId === pendingRecordIdRef.current;
        if (
          !addressedByRecord &&
          payload.calculationId !== pendingCalcIdRef.current
        ) {
          return;
        }
        mergeExecutiveSummary(payload.data);
      };
      // Behind the same guard as the terminal handler, preserving today's behaviour: both are
      // payload-consuming handlers for a BE that announced no events at all.
      if (eventName) {
        bindTracked(channel, 'executive_summary_updated', summaryHandler);
      }

      // The authoritative read every recovery trigger converges on (ONCALL-937). It reads
      // `eval_result` and lets `resolveCompletion` decide whether what came back belongs to
      // this run; it carries no state of its own and cannot report a failure, so no trigger
      // can put anything on screen the server did not produce.
      //
      // Note what is NOT wired to it: `calculation_result`. That handler is bound above and
      // consumes its payload (the warnings, the error message, and the uuid the start job
      // assigns on dispatch), so the event stays this page's own business. What the shared
      // arming below adds is the two triggers this page had no answer for — the subscription
      // being registered, and the poll.
      const reconcile = async () => {
        if (!pendingRecordIdRef.current) return;

        console.debug(
          '[Eval3 poll] checking eval_result for calcId=%d',
          calcId
        );
        try {
          const raw = await fetchCalculationResult(calcId, 'yearly');
          const firstResult = (raw as AggregatedFreeCashFlowResultResponse)
            .results?.[0];
          if (firstResult?.result && firstResult?.request) {
            console.debug(
              '[Eval3 poll] results available, resolving calcId=%d',
              calcId
            );
            resolveCompletion([], firstResult.calculationId);
          }
        } catch {
          // EvalCalculator unreachable — swallow; the next trigger retries, or the cap ends
          // the wait.
        }
      };

      // TWO clocks, deliberately, because they answer different questions (ONCALL-937).
      //
      // `createdAt` is the RECORD's creation time, and a re-run does NOT rewrite it
      // (`reuse_existing_calculation` rewrites status, dispatched_at and updated_at — never
      // created_at). It is still the right basis for the GRACE PERIOD: resuming a run whose
      // record is old means we have already been waiting, so skipping the wait is correct.
      //
      // It is the wrong basis for the CAP. Resuming a perfectly healthy re-run of an
      // evaluation created over 30 minutes ago would blow the cap on the very first tick,
      // report a timeout within one round trip, and then unsubscribe — so the real completion
      // could never land. The cap therefore measures from when THIS subscription started
      // waiting (`armJobCompletion` always measures `maxMs` from arming time), which also puts
      // the two ends in agreement: the server's own watchdog measures `updated_at`, which a
      // re-run DOES rewrite.
      //
      // A malformed or absent timestamp parses to NaN, and `Math.max(0, NaN)` is NaN —
      // `setTimeout(NaN)` fires immediately, so a bad value would silently switch the grace
      // period off. That guard used to live here; it now lives in `armJobCompletion`, which
      // treats a non-finite or non-positive `since` as "not supplied" and serves the full
      // delay. Duplicating it here would make BOTH copies untestable from either side — each
      // would mask the other — so there is exactly one, at the boundary every caller crosses
      // (ONCALL-937 review, C3).
      const recordCreatedAtMs = new Date(createdAt).getTime();

      disarmRef.current = armJobCompletion({
        channel,
        reconcile,
        poll: {
          delayMs: STUCK_CHECK_DELAY_MS,
          intervalMs: POLL_INTERVAL_MS,
          maxMs: MAX_POLL_DURATION_MS,
          since: recordCreatedAtMs,
          onExpired: () => {
            // Resolving releases this token, and a newer subscription mints its own. Still
            // holding OURS means the read found no results — or found results this run may
            // not claim — so the run is unresolved and the cap governs it. `resolveError`
            // tests the token again; this one keeps the log honest too.
            if (runTokenRef.current !== runToken) return;
            console.debug(
              '[Eval3 poll] max duration exceeded, calcId=%d',
              calcId
            );
            // Reached only AFTER a read ran (ONCALL-937). The cap used to be checked first and
            // returned WITHOUT reading, so a run that had in fact completed — its Pusher event
            // missed — was reported to the user as a timeout, and users re-ran a calculation
            // that had already succeeded.
            //
            // Note the read may also have THROWN (eval_result 404s the pre-dispatch window and
            // 500s on its own blanket rescue), in which case we time out on no evidence rather
            // than on evidence of no results. That is no worse than the old behaviour, which
            // never read at all — but it is the remaining way this message can be wrong, and
            // suppressing it would trade a false failure for an unbounded spinner.
            resolveError('Calculation timed out waiting for results');
          },
        },
      });
    },
    [
      setSelectedCalcId,
      setCalcResultReadyId,
      setAppliedConfig,
      onRefetchCalculations,
      onWarning,
      fetchCalculationResult,
      mergeExecutiveSummary,
      bindTracked,
      unbindAll,
      disarmRecovery,
    ]
  );

  /**
   * Starts a new calculation via POST and subscribes to Pusher for the result.
   *
   * @param {CalculateRequest} config - The full calculate request payload.
   * @param {CalculationConfig} [configOverride] - Optional config snapshot to
   *   use as the applied config on completion. When omitted, the current
   *   reactive config is used. Pass this when the caller has just updated
   *   atoms that haven't re-rendered yet (e.g. workspace creation with a
   *   new eval2Id from the modal).
   * @return {Promise<void>}
   */
  const triggerCalculation = useCallback(
    async (config: CalculateRequest, configOverride?: CalculationConfig) => {
      setIsCalculating(true);
      setError(null);
      appliedConfigSnapshotRef.current =
        configOverride ?? currentConfigRef.current;
      previousAppliedConfigRef.current = appliedConfig;
      try {
        const response = await triggerCalculate(config);
        setAppliedConfig(appliedConfigSnapshotRef.current);
        setSelectedCalcId(response.id);
        onRefetchCalculations(config.workspace_id);
        // ONE predicate for "whose uuid is this?", the same one the server uses. Both call
        // sites of `subscribeToPusher` ask that question, and they used to answer it
        // differently — `kind === 'rerun'` here, `dispatched` on the resume path. `dispatched`
        // is the honest test and `kind` was a proxy for it; `show.json.jbuilder` says so
        // outright ("The phase test is `dispatched?`, NOT `calculation_id.presence`"), and
        // `calculate.json.jbuilder` emits `run.dispatched` for exactly this.
        //
        // In the 202 it is false by construction — the run dispatches from the job, not from
        // the request that returned this — so the behaviour is unchanged for both `initial`
        // (uuid is nil anyway) and `rerun` (uuid is the previous run's, marked superseded).
        // What changes is that the answer no longer depends on a proxy that happens to agree:
        // a `calculationId` present on an `initial` run would previously have been trusted as
        // THIS run's while still pre-dispatch, which is the precise mistake the server's
        // comment warns against.
        //
        // A response with NO `run` descriptor at all reads as DISPATCHED, which is the
        // opposite default from the resume path, deliberately. None exists today
        // (`calculate.json.jbuilder` always emits the block), but one would predate INS-12398,
        // when eval-start was synchronous and the 202 genuinely did carry this run's uuid.
        // Reading it as not-dispatched would file a perfectly good address as SUPERSEDED and
        // then refuse this run's own completion events — a hang. On the RESUME path the
        // not-dispatched default is safe, because withholding the uuid there only defers it:
        // the poll's record fallback and the record-addressed event branch still resolve the
        // run. Same predicate, opposite failure modes on absence, so opposite defaults.
        const dispatched = response.run ? response.run.dispatched : true;
        subscribeToPusher(
          response.id,
          // Pre-dispatch the 202's `calculationId` is not this run's: on a re-run it is the
          // PREVIOUS run's, kept so its results stay readable while this one computes
          // (INS-12398). Seeding the stale-event guard with it would make every late event
          // about that superseded run look like this run's. So: no uuid for this run yet, and
          // that one explicitly marked as superseded.
          dispatched ? response.calculationId : null,
          response.notifications,
          new Date().toISOString(),
          config.workspace_id,
          dispatched ? null : response.calculationId
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Calculation failed');
        setIsCalculating(false);
      }
    },
    [
      triggerCalculate,
      subscribeToPusher,
      appliedConfig,
      setAppliedConfig,
      setSelectedCalcId,
      onRefetchCalculations,
    ]
  );

  /**
   * Subscribes to Pusher for a pending calculation (e.g. from tile click).
   * The caller provides notifications from the calculation detail response.
   * Also schedules a stuck-check timeout based on when the calculation was created.
   *
   * @param {number} calcId - The numeric calculation ID.
   * @param {string} calculationId - The detail's `calculationId` (see the note on `dispatched`).
   * @param {PusherNotifications} notifications - Channel and event info.
   * @param {string} createdAt - ISO timestamp of when the calculation was created.
   * @param {number} workspaceId - The detail's `workspaceId`: whose calculation list to
   *   refresh once this run completes, so the nav tiles leave Processing.
   * @param {boolean} [dispatched] - The detail's `dispatched` flag: whether the CURRENT run
   *   has handed off to the calculator, and therefore whether `calculationId` is its address.
   */
  const resumePendingCalculation = useCallback(
    (
      calcId: number,
      // Whose uuid this is depends entirely on `dispatched` below (INS-12398).
      calculationId: string | null,
      notifications: PusherNotifications,
      createdAt: string,
      workspaceId: number,
      dispatched?: boolean
    ) => {
      setIsCalculating(true);
      setError(null);
      appliedConfigSnapshotRef.current = currentConfig;
      // The detail's `calculationId` means two opposite things depending on the phase, and
      // until now this passed it through as though it always meant the first (ONCALL-937):
      //   dispatched  -> it IS this run's address; seed the stale-event guard with it.
      //   !dispatched -> it is the run this one REPLACED, deliberately left in place so those
      //                  results stay readable. Seeding with it would make every late event
      //                  about the superseded run look like this run's, and would let the
      //                  recovery poll stamp the old run's uuid onto this run's results — the
      //                  uuid every cashflows-by-month read then resolves by. So: no uuid for
      //                  this run yet, and that one explicitly marked as superseded, exactly
      //                  as `triggerCalculation` does for a re-run.
      // `ensure_calculation_id!` also 404s that window server-side, so this is the client half
      // of a guard that already holds — but the refusal was inert on this path without it.
      subscribeToPusher(
        calcId,
        dispatched ? calculationId : null,
        notifications,
        createdAt,
        workspaceId,
        dispatched ? null : calculationId
      );
    },
    [subscribeToPusher, currentConfig]
  );

  /**
   * Cancels the current calculation by unsubscribing from Pusher and disarming the
   * recovery triggers (the subscribe trigger and the poll).
   */
  const cancelCalculation = useCallback(() => {
    if (subscriptionRef.current) {
      console.debug(
        '[Eval3 Pusher] cancelCalculation — unsubscribing, pendingCalcId=%s',
        pendingCalcIdRef.current
      );
    }
    // Unbind BEFORE dropping the reference — see the same note in `resolveError`.
    unbindAll();
    disarmRecovery();
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
    pendingCalcIdRef.current = null;
    pendingRecordIdRef.current = null;
    runTokenRef.current = null;
    setIsCalculating(false);
    setError(null);
  }, [unbindAll, disarmRecovery]);

  return {
    isCalculating,
    error,
    calculationWarnings,
    triggerCalculation,
    resumePendingCalculation,
    cancelCalculation,
  };
};

// ---------------------------------------------------------------------------
// useUnsavedChanges — blocks navigation when config changes are uncalculated.
// ---------------------------------------------------------------------------

/**
 * Hook that prevents users from losing unsaved configuration changes.
 * Guards in-page navigation (workspace switch, tile click) by deferring
 * the action and exposing modal state for the consumer to render a
 * confirmation dialog.
 *
 * @param {number} packageId - The package ID.
 * @return {object} Modal state and navigation guards.
 */
export const useUnsavedChanges = (
  packageId: number
): {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  confirmDiscard: () => void;
  guardNavigation: (callback: () => void) => void;
} => {
  const [showModal, setShowModal] = useState(false);
  const pendingCallbackRef = useRef<(() => void) | null>(null);

  const isDirty = useAtomValue(isDirtyAtom(packageId));

  /**
   * Guards in-page navigation (workspace switch, tile click). If dirty,
   * defers the callback and shows the confirmation modal. If clean,
   * executes the callback immediately.
   *
   * @param {Function} callback - The navigation action to guard.
   */
  const guardNavigation = useCallback(
    (callback: () => void) => {
      if (isDirty) {
        pendingCallbackRef.current = callback;
        setShowModal(true);
      } else {
        callback();
      }
    },
    [isDirty]
  );

  /**
   * Confirms discarding unsaved changes. Executes the deferred
   * navigation callback and closes the modal. The callback itself
   * (e.g. tile click) is responsible for hydrating new config and
   * resetting dirty state.
   */
  const confirmDiscard = useCallback(() => {
    pendingCallbackRef.current?.();
    pendingCallbackRef.current = null;
    setShowModal(false);
  }, []);

  return {
    showModal,
    setShowModal,
    confirmDiscard,
    guardNavigation,
  };
};

// ---------------------------------------------------------------------------
// useExportAries — exports ARIES queries as a ZIP download via background job.
// ---------------------------------------------------------------------------

/**
 * Hook that manages the ARIES export lifecycle via Pusher notifications.
 * Uses the shared useServerExport pattern: POST enqueues a Sidekiq job,
 * subscribes to Pusher for completion, auto-opens signed download URL.
 *
 * @param {number | null} selectedCalcId - The selected calculation ID.
 * @param {Function} onError - Callback invoked with an error message string on failure.
 * @return {object} Export action and loading state.
 */
export const useExportAries = (
  selectedCalcId: number | null,
  onError: (msg: string) => void
): {
  exportAries: () => Promise<void>;
  isExportAriesLoading: boolean;
} => {
  const { startExport, isExporting, exportStatus } = useServerExport({
    channel: `eval_calculator_${selectedCalcId}`,
    event: 'aries_export_complete',
    exportFn: async () => {
      if (!selectedCalcId) throw new Error('No calculation selected');

      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      const response = await fetch(
        `/evaluations/calculations/${selectedCalcId}/downloads/aries_export`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Export failed (${response.status})`);
      }

      const data = await response.json();
      return { exportId: data.export_id };
    },
  });

  useEffect(() => {
    if (exportStatus.status === 'failed' && exportStatus.error) {
      onError(exportStatus.error);
    }
  }, [exportStatus.status, exportStatus.error, onError]);

  const exportAries = useCallback(async () => {
    if (!selectedCalcId) return;
    await startExport();
  }, [selectedCalcId, startExport]);

  return { exportAries, isExportAriesLoading: isExporting };
};

// ---------------------------------------------------------------------------
// useEvalExportPresets — resolves named export presets for a single export type.
// ---------------------------------------------------------------------------

/**
 * Returns the fully resolved export preset list for one Eval 3 export type,
 * including the "Current View Format" sentinel stamped with the current user's
 * filename suffix. Falls back to an empty array while the atom is loading or
 * if the fetch fails (the export button remains functional with no dropdown).
 *
 * @param {string} slice - Which export type to read ('slots', 'slotAllocations', 'cashflows').
 * @param {string} format - Output format for the Current View sentinel.
 * @return {ExportPreset[]} Ordered preset list ready to pass as `exportPresets`.
 */
export const useEvalExportPresets = (
  slice: keyof EvalExportPresets,
  format: ExportFormat
): ExportPreset[] => {
  const presetsData = useLoadableAtomValue(evalExportPresetsLoadableAtom);
  const session = useAtomValue(sessionResourceAtom);
  const username = session?.profile?.name ?? undefined;
  return presetsData.state === 'hasData'
    ? withCurrentViewPreset(presetsData.data[slice], format, username)
    : [];
};
